import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AttributeToken } from '@sleed/bizcommerce-api';
import { attributesApi } from '~/lib/api/client';
import { uploadAttributeImage } from '~/lib/api/attribute-image-upload';
import { formToToken, type AttributeFormValues } from '~/lib/schemas/attribute';

/**
 * Staged image changes for the two attribute slots. A `File` uploads (overwriting that slot);
 * a `delete` flag removes the existing image. The orchestrator writes the entity first (so the id
 * exists / the imgdel flags apply), then uploads staged files against the real id.
 */
export interface AttributeImagePayload {
	slot1?: File | null;
	slot2?: File | null;
	delete1?: boolean;
	delete2?: boolean;
}

interface AttributeSavePayload {
	form: AttributeFormValues;
	images?: AttributeImagePayload;
}

async function uploadStagedSlots(id: number | undefined, images?: AttributeImagePayload) {
	if (!id || !images) return;
	if (images.slot1) await uploadAttributeImage(id, '1', images.slot1);
	if (images.slot2) await uploadAttributeImage(id, '2', images.slot2);
}

export const attributeKeys = {
	all: ['attributes'] as const,
	list: () => [...attributeKeys.all, 'list'] as const
};

/**
 * Attributes list. Mirrors the legacy admin grid's `/attributes/getall` (loadMode 'raw'):
 * the endpoint returns the full set in an `AttributeTokenList.items` envelope — no paging.
 */
export function useAttributesList() {
	return useQuery<AttributeToken[]>({
		queryKey: attributeKeys.list(),
		queryFn: async () => (await attributesApi.getAll()).data.items ?? []
	});
}

/** Create / update / delete an attribute (parity with the legacy CustomStore insert/update/remove). */
export function useAttributeMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: attributeKeys.all });

	const create = useMutation({
		mutationFn: async ({ form, images }: AttributeSavePayload) => {
			const token = { ...formToToken(form), imgdel1: Boolean(images?.delete1), imgdel2: Boolean(images?.delete2) };
			const created = (await attributesApi.insert(token)).data;
			await uploadStagedSlots(created.id ?? form.id, images);
			return created;
		},
		onSuccess: invalidate
	});

	const update = useMutation({
		mutationFn: async ({ form, images }: AttributeSavePayload) => {
			const token = { ...formToToken(form), imgdel1: Boolean(images?.delete1), imgdel2: Boolean(images?.delete2) };
			const updated = (await attributesApi.update(token)).data;
			await uploadStagedSlots(updated.id ?? form.id, images);
			return updated;
		},
		onSuccess: invalidate
	});

	const remove = useMutation({
		mutationFn: async (id: number) => (await attributesApi.remove(id)).data,
		onSuccess: invalidate
	});

	return { create, update, remove };
}
